import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import userRightsImage from '../images/user_rights_management.png';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { api } from '../api';
import { oauthService } from '../oauth';
import { resolveDocumentTarget } from '../utils/document-target';

/**
 * True for resources the document.write() pipeline can safely handle (HTML topic
 * pages, plus markdown-sourced pages that CloudHub renders as text/html under a .md
 * URL) — no extension (defaults to index.html) or an explicit .html/.htm/.md extension.
 * Everything else (PDFs, downloads, images requested directly, etc.) must NOT be
 * fetched as text: forcing responseType:'text' on binary bytes is lossy (invalid byte
 * sequences become U+FFFD replacement chars) and document.write()-ing the corrupted
 * result tries to parse it as HTML instead of letting the browser render/download it
 * natively. See docs/decision-records — binary resources are instead navigated to
 * directly via window.location.replace() in loadDocument() below. Must stay in sync
 * with the identical helper in backend/src/routes.ts.
 */
function isHtmlLikeResource(resource: string): boolean {
  const lastSegment = resource.toLowerCase().split(/[?#]/)[0].split('/').pop() || '';
  const dotIndex = lastSegment.lastIndexOf('.');
  if (dotIndex === -1) {
    return true;
  }
  const extension = lastSegment.slice(dotIndex + 1);
  return extension === 'html' || extension === 'htm' || extension === 'md';
}


/**
 * Minimal fullscreen document viewer - no frame app styling/header
 * Opens in new tab from catalog
 * Uses session cookies for authentication
 *
 * Serves BOTH URL shapes, which are normalised into a single DocumentTarget by
 * resolveDocumentTarget() before anything else runs:
 *   /{slug}/*                                     published content
 *   /application/{appId}/{slug}/{version}/*        preview of a pinned revision
 * Nothing below this line should branch on which shape was used.
 */
export function DocumentViewer() {
  const navigate = useNavigate();
  const { uuid, name, version, appNameSlug, '*': resourcePath } = useParams<{
    uuid: string;
    name: string;
    version: string;
    appNameSlug: string;
    '*': string;
  }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(() => {
    try {
      return localStorage.getItem('auth_dialog_dismissed') === 'true';
    } catch (e) {
      return false;
    }
  });

  // React 18 StrictMode (dev only) intentionally mounts every component twice
  // (mount -> cleanup -> mount) to surface effect bugs. Without this guard,
  // loadDocument() below would run twice concurrently, and both invocations
  // would call document.open()/write()/close() on the SAME real document,
  // racing against each other and silently corrupting the resulting DOM/script
  // execution (no thrown error — the page just ends up broken, e.g. TOC never
  // populates). A simple shared boolean flag does NOT work here: StrictMode
  // reuses the same ref across both invocations, so the second invocation would
  // reset it back to false before the first invocation's async continuation
  // gets a chance to check it. A monotonically increasing generation counter,
  // captured per-invocation and compared against the CURRENT ref value, is
  // required so only the most recent invocation is ever allowed to write.
  const requestGenerationRef = useRef(0);

  useEffect(() => {
    const myGeneration = ++requestGenerationRef.current;
    loadDocument(myGeneration);
  }, [uuid, name, version, appNameSlug, resourcePath]);

  // Human-readable product name for the auth dialog. Derived from whichever slug the
  // URL carried, so it is available before (and without) resolving the application — the
  // dialog's whole purpose is to appear when the user has no session and the content
  // request 401'd.
  const displayName = (name || appNameSlug || '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());

  function handleGoBack() {
    try {
      // Notify other windows/tabs to navigate to home
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({ type: 'NAVIGATE_HOME' }, window.location.origin);
      }

      try {
        const channel = new BroadcastChannel('app_nav_channel');
        channel.postMessage({ type: 'NAVIGATE_HOME' });
        channel.close();
      } catch (e) {
        // BroadcastChannel may not be supported
      }

      // Try to close this tab; if not allowed, navigate to '/'
      setTimeout(() => {
        try {
          window.close();
        } catch (e) {
          navigate('/');
        }
      }, 150);
    } catch (e) {
      navigate('/');
    }
  }

  async function loadDocument(myGeneration: number) {
    let documented = false;
    try {
      setLoading(true);
      setError(null);

      // Normalise both URL shapes into one target up front. Everything after this point
      // is scheme-agnostic. A null target means the URL names no document we know about
      // (unknown or missing slug) — a genuine 404, which this route must render itself
      // because /:appNameSlug/* is the app's catch-all.
      //
      // The application list is used here as an identity source only (slug -> appId), so
      // it is served from the short-lived sessionStorage cache: every in-document TOC
      // click is a full page load (document.write() replaces the whole document), and
      // re-fetching the list each time added an upstream round trip to every navigation.
      let target = await resolveDocumentTarget(
        { uuid, name, version, appNameSlug, resourcePath },
        () => api.getApplicationsCached()
      );

      if (requestGenerationRef.current !== myGeneration) {
        return;
      }

      // A slug absent from a CACHED list may simply have been published within the TTL,
      // so never report 404 on cached data — drop the cache and re-resolve against a
      // fresh list before giving up.
      if (!target && api.hasCachedApplications()) {
        api.invalidateApplicationsCache();
        target = await resolveDocumentTarget(
          { uuid, name, version, appNameSlug, resourcePath },
          () => api.getApplicationsCached()
        );

        if (requestGenerationRef.current !== myGeneration) {
          return;
        }
      }

      if (!target) {
        setError('Document Not Found');
        return;
      }

      const { appId, appNameSlug: slug, version: resolvedVersion, resourcePath: resource, baseNavPath } = target;

      if (!isHtmlLikeResource(resource)) {
        // Binary/non-HTML resource (e.g. a PDF linked from within a topic page). Pre-check
        // access via the existing lightweight endpoint first, so an expired/missing
        // session still surfaces the SAME auth dialog/error UX as HTML topics below (via
        // the catch block) instead of the browser showing a raw JSON error after
        // navigating away. If access is fine, navigate the browser to the resource's URL
        // on the BACKEND origin so the backend serves the bytes natively (PDF viewer,
        // download, etc.).
        await api.checkContentAccess(appId);

        if (requestGenerationRef.current !== myGeneration) {
          return;
        }

        const resourceUrl = api.getApplicationResourceUrl(baseNavPath, resource);
        const resolved = new URL(resourceUrl, window.location.href);
        if (resolved.origin === window.location.origin && resolved.pathname === window.location.pathname) {
          // The backend served the SPA for this very URL instead of the resource, so
          // replacing to it would reload this page forever.
          setError('Document Not Found');
          return;
        }

        documented = true;
        window.location.replace(resourceUrl);
        return;
      }

      // Fetch HTML through backend proxy
      // Backend will:
      // 1. Check session cookie for authentication
      // 2. Validate access with CloudHub API
      // 3. Rewrite URLs to route through proxy
      let html = await api.getDocumentationHtml(appId, slug, resolvedVersion, resource);

      // If a newer invocation has started (StrictMode's dev-only double-invoke,
      // or a fast navigation to different params) since this one began, bail out
      // — the newer invocation owns the document now.
      if (requestGenerationRef.current !== myGeneration) {
        return;
      }

      // CSP nonce handoff: the proxied docs HTML was nonce-stamped by the backend's
      // onSend hook with the nonce of the /api/v1/docs/... RESPONSE — but CSP headers
      // on a fetch() response are inert, and document.write() below renders this HTML
      // into the CURRENT document, which stays governed by the SPA shell's own CSP
      // (with the shell's own nonce). Re-stamp the docs HTML with the shell's nonce so
      // its inline <script> tags (e.g. lima's inline location redirect) are authorized.
      // No-op in dev (the Vite shell has no CSP/meta) and when either nonce is missing.
      // Base64 nonces are HTML-attribute-safe and collision-free, so a plain string
      // replace of every occurrence is sufficient.
      const shellNonce = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content');
      if (shellNonce) {
        const apiNonce = html.match(/<meta name="csp-nonce" content="([^"]+)"/)?.[1];
        if (apiNonce && apiNonce !== shellNonce) {
          html = html.split(apiNonce).join(shellNonce);
        }
      }

      // Replace the entire page with the fetched document HTML.
      // document.write() is used instead of srcdoc iframe so that:
      // - window.location stays as /application/... (correct URL for MadCap navigation JS)
      // - Relative asset URLs resolve correctly via the injected <base> tag
      // - Browser handles compression (Brotli/gzip) natively for all sub-resources
      // - Session cookies survive (browser-stored, sent on all same-origin requests)
      documented = true;
      document.open();
      document.write(html);
      document.close();
      // React app is replaced — do not update state after this point
      return;
    } catch (err: any) {
      if (requestGenerationRef.current !== myGeneration) {
        return;
      }
      // Log errors except 401 (expected when not authenticated)
      if (err.response?.status !== 401) {
        console.error('Error loading document:', err);
      }
      
      if (err.response?.status === 401) {
        // Not authenticated - either auto-login if user opted out of dialog, or show dialog
        setError('Authentication Required');
        if (dontShowAgain) {
          // proceed directly to login
          // Mark that this window was opened from catalog (for postMessage after auth)
          sessionStorage.setItem('opened_from_catalog', 'true');
          const returnPath = window.location.pathname;
          oauthService.startLogin(returnPath);
        } else {
          setShowAuthDialog(true);
        }
      } else if (err.response?.status === 403) {
        setError('Access Denied');
      } else if (err.response?.status === 429) {
        setError('Rate Limit Exceeded');
      } else if (err.response?.status === 404) {
        setError('Document Not Found');
      } else if (err.response?.status === 502) {
        setError('Service Unavailable');
      } else {
        const detail = err.response?.data?.message;
        setError(detail ? `Failed to load document: ${detail}` : 'Failed to load document. Please try again.');
      }
    } finally {
      if (!documented && requestGenerationRef.current === myGeneration) setLoading(false);
    }
  }

  function handleAuthDialogProceed() {
    try {
      if (dontShowAgain) {
        localStorage.setItem('auth_dialog_dismissed', 'true');
      } else {
        localStorage.removeItem('auth_dialog_dismissed');
      }
    } catch (e) {
      // ignore storage errors
    }

    setShowAuthDialog(false);

    // Mark that this window was opened from catalog (for postMessage after auth)
    sessionStorage.setItem('opened_from_catalog', 'true');

    // Trigger OAuth login - will redirect to auth and back to this page
    const returnPath = window.location.pathname;
    oauthService.startLogin(returnPath);
  }

  // Loading state
  if (loading) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff'
      }}>
        <ProgressSpinner />
      </div>
    );
  }

  // Error state
  if (error) {
    // For authentication errors, just show spinner (no error message)
    // Dialog will be shown separately if showAuthDialog is true
    if (error === 'Authentication Required') {
      return (
        <>
          <div style={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#ffffff'
          }}>
            <ProgressSpinner />
          </div>
          
          {/* Authentication Required Dialog - can appear over spinner */}
          <Dialog
            header="Account required"
            visible={showAuthDialog}
            onHide={() => setShowAuthDialog(false)}
            modal
            dismissableMask={false}
            draggable={false}
            resizable={false}
            style={{ width: '90%', maxWidth: '450px' }}
            contentStyle={{ padding: '1rem 1.25rem' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <p style={{ margin: '0 0 0.25rem 0', color: '#374151', fontSize: '1rem', lineHeight: '1.4' }}>
                To access the user manual of <strong>{displayName}</strong>, please log in or register for an account via <a href="https://marketplace.pantaris.io/about-us" target="_blank" rel="noopener noreferrer" style={{ color: '#164293', textDecoration: 'underline' }}>ETAS Cloud Hub</a>.
              </p>

              <p style={{ margin: 0, color: '#374151', fontSize: '0.95rem', lineHeight: '1.4', marginTop: '0.5rem' }}>
                Need assistance? Contact our <a href="https://www.etas.com/ww/en/support-training/support-hotlines/" target="_blank" rel="noopener noreferrer" style={{ color: '#164293', textDecoration: 'underline' }}>ETAS Support</a>.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Checkbox
                  inputId="dont-show"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.checked ?? false)}
                />
                <label htmlFor="dont-show" style={{ fontSize: '0.875rem', color: '#6B7280', cursor: 'pointer' }}>
                  Don't show this message again
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  label="Proceed"
                  onClick={handleAuthDialogProceed}
                  autoFocus
                />
              </div>
            </div>
          </Dialog>
        </>
      );
    }

    if (error === 'Rate Limit Exceeded') {
      return (
        <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', padding: '24px', maxWidth: '900px', width: '100%', boxSizing: 'border-box' }}>
            <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#b45309' }}>429</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#111' }}>Too Many Requests</div>
              <div style={{ color: '#444', fontSize: '16px' }}>The document service is temporarily rate limiting requests. Please wait a moment and try again.</div>
            </div>
          </div>
        </div>
      );
    }
    
    // For access denied, render a 403 layout
    if (error === 'Access Denied') {
      return (
        <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', padding: '24px', maxWidth: '900px', width: '100%', boxSizing: 'border-box' }}>
            <div style={{ flex: '0 0 auto' }}>
              <img src={userRightsImage} alt="Access Denied" style={{ width: '220px', height: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{
                fontSize: '28px',
                fontWeight: 700,
                background: 'linear-gradient(90deg, #164293 0%, #0066cc 25%, #006633 50%, #cc0066 75%, #89037a 100%)',
                WebkitBackgroundClip: 'text' as any,
                backgroundClip: 'text',
                color: 'transparent'
              }}>403</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#111' }}>Access Denied</div>
              <div style={{ color: '#444', fontSize: '16px' }}>The page you are trying to open has restricted access.</div>
              <div style={{ color: '#444', fontSize: '16px' }}>
                Our <a href="https://www.etas.com/ww/en/support-training/support-hotlines/" target="_blank" rel="noopener noreferrer" style={{ color: '#164293', textDecoration: 'underline' }}>customer support</a> team is ready to help.
              </div>

              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-start' }}>
                <Button label="Go back" onClick={handleGoBack} />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // For other errors, show generic error state
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fafafa',
        padding: '2rem'
      }}>
        <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#d32f2f', marginBottom: '1rem' }}></i>
        <h2 style={{ color: '#333', marginBottom: '0.5rem' }}>{error}</h2>
        <p style={{ color: '#666', textAlign: 'center', maxWidth: '500px' }}>
          {error === 'Document Not Found' && 'The requested document could not be found.'}
          {error !== 'Document Not Found' && 'Please try again or contact support if the problem persists.'}
        </p>
      </div>
    );
  }

  // Success: document.write() has already replaced the page — this is never rendered.
  return null;
}
